// Contract Orders, заказы в договорах
import React, { useEffect, useState } from "react";
import Loader from 'components/Common/Loader';
import { Table, Button, Modal, Input, Form, Radio, Row, Col, Typography } from 'antd';
import { addContractOrderPayment } from 'services'; // оплата заказов

// Zustand store
import { useShallow } from 'zustand/react/shallow';
import { useContractOrdersStore, useUserStore } from 'storeZustand';

import { useContractOrdersColumns, useToggle, useContractOrdersFilters, useStatusPaymentOrders, useRowSelection } from 'hooks';
import { DangerBtn, PrimaryBtn, SecondaryBtn } from 'components';
import SelectExternalCompany from './selectExternalCompany';
import CustomRadio from 'components/Common/CustomRadio';
import { formatIsoTimeToString } from 'helpers/Date/formatDate';
import OutstaffDashboard from './dashboard/outstaffDashboard';
import SelectManager from './selectManager';

const ContractOrders = () => {
	const [form] = Form.useForm();
	const { Text } = Typography;
	const [modal, toggleModal] = useToggle(false);
	const [loadingBtn, setLoadingBtn] = useState(false);

	const { applyFilters } = useContractOrdersFilters(); // применить фильтры заказов

	// Статус оплаты заказов
	const { statusPayment, paymentOptions, handleStatusPaymentChange } = useStatusPaymentOrders();

	// выбранные строки в таблице и общая сумма
	const { selectedRows, rowSelection, totalPaymentAmount, setSelectedRowKeys } = useRowSelection();

	// колонки в таблице заказов
	const columns = useContractOrdersColumns();

	// Пользователь
	const [staffId] = useUserStore(useShallow((state) => [state.staffId]));

	// Заказы
	const [
		contractOrdersList,
		getContractOrders,
		loading,
		resetFilters,
		addOrderFilter,
		orderFilters
	] = useContractOrdersStore(useShallow((state) => [
		state.contractOrdersList,
		state.getContractOrders,
		state.loading,
		state.resetFilters,
		state.addOrderFilter,
		state.orderFilters
	]));

	useEffect(() => {
		getContractOrders();
	}, []);

	const onFinish = async (values) => {
		setLoadingBtn(true);
		let remainingAmount = values.amount; // введенная сумма

		// цикл оплат
		for (const row of selectedRows) {
			const paymentData = {
				amount: row.amount,
				payment_type: values.payment_type,
				date_of_payment: formatIsoTimeToString(new Date()),
				contract_order: row.id,
				creator: staffId,
			};

			if (remainingAmount <= 0) {
				break;
			}

			// если суммы полностью не хватает на оплату заказа, она начисляется частично
			if (row.amount <= remainingAmount) {
				await addContractOrderPayment(paymentData);
				remainingAmount -= row.amount;
			} else {
				paymentData.amount = Number(remainingAmount);
				await addContractOrderPayment(paymentData);
				break;
			}
		}

		setTimeout(() => {
			setSelectedRowKeys([]);
			setLoadingBtn(false);
			getContractOrders();
			toggleModal();
		}, 1000);
	};

	// Добавить оплату
	const handleAddPayment = () => {
		toggleModal();
	};

	return (
		<>
			<div className="mb-2">
				<OutstaffDashboard />
			</div>
			<div className="d-flex gap-1 justify-content-between mb-2">
				<div className="d-flex gap-1">
					<div style={{ width: '180px' }}>
						{/* Выбор менеджера */}
						<SelectManager />
					</div>
					<div style={{ width: '180px' }}>
						{/* Выбор внешней компании */}
						<SelectExternalCompany />
					</div>
					<div style={{ width: '110px' }}>
						{/* Поле ввода номера договора для фильтрации */}
						<Input type='number' placeholder="№ договора" onChange={(e) => addOrderFilter({ contract_number: e.target.value })} />
					</div>
					<div>
						{/* Выбор оплачено или неоплачено */}
						<CustomRadio
							value={statusPayment}
							onChange={handleStatusPaymentChange}
							options={paymentOptions}
						/>
					</div>
				</div>
				<div className="d-flex gap-1">
					{/* Кнопки применить и сбросить фильтры */}
					<SecondaryBtn onClick={applyFilters}>Применить</SecondaryBtn>
					<DangerBtn onClick={resetFilters}>Сбросить</DangerBtn>
					<PrimaryBtn onClick={handleAddPayment}>
						Добавить оплату
					</PrimaryBtn>
				</div>
			</div>

			{loading && <Loader />}
			{!loading && contractOrdersList && contractOrdersList?.length > 0 && (
				<Table
					rowSelection={rowSelection}
					columns={columns}
					dataSource={contractOrdersList}
					size='small'
					rowKey="id"
				/>
			)}
			<Modal
				title='Создать оплату'
				open={modal}
				onCancel={toggleModal}
				width={365}
				footer={null}
			>
				<Form
					onFinish={onFinish}
					autoComplete="off"
					form={form}
					fields={[
						{
							"name": [
								"amount"
							],
							"value": totalPaymentAmount
						},
						{
							"name": [
								"payment_type"
							],
							"value": 1
						},
					]}
				>
					<div className="mb-3">
						<label className="form-label archi-label me-1">Общая сумма оплаты - 		<Text type="danger">{totalPaymentAmount}</Text>
						</label>
					</div>
					<Row gutter={16}>
						<Col span={10}>
							<label className="form-label archi-label me-1">Сумма</label>
							<Form.Item
								name="amount"
								rules={[
									{
										required: true,
										message: "Введите сумму",
									},
									{
										validator: (_, value) => {
											if (value && Number(value) > totalPaymentAmount) {
												return Promise.reject(new Error("Сумма превышает общую сумму оплаты"));
											}
											return Promise.resolve();
										},
									},
								]}
								validateTrigger={["onBlur", "onChange"]}
							>
								<Input type="number" />
							</Form.Item>
						</Col>
						<Col span={14}>
							<label className="form-label archi-label me-1">Тип оплаты</label>
							<Form.Item name="payment_type">
								<Radio.Group size="large" >
									<Radio value={1}>Наличн</Radio>
									<Radio value={2}>Безнал</Radio>
								</Radio.Group>
							</Form.Item>
						</Col>
					</Row>
					<div className="text-end">
						<Form.Item noStyle={true}>
							<Button type="primary" htmlType="submit" loading={loadingBtn}>
								Оплатить
							</Button>
						</Form.Item>
					</div>
				</Form>
			</Modal>
		</>
	)
}

export default ContractOrders; 